/**
 * RollupChart — Score progression over time with deployment markers.
 * 4 lines: SEO (blue), AEO (amber), GEO (green), Overall (ink/slate).
 * Deploy markers = vertical dashed reference lines.
 * Spec §8.5, visual ref 01-desktop.html / 02-mobile.html.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuditResults = Record<string, any>;

interface RollupAttribution {
  deployedAt: string;
  title: string;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  audits: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributions: Record<string, any>[];
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function RollupChart({ audits, attributions }: Props) {
  if (audits.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
        No audit data in this range
      </div>
    );
  }

  const data = audits.map((a) => ({
    date: fmtDate(a.startedAt ?? a.completedAt ?? ''),
    seo: a.scores?.seo?.value ?? null,
    aeo: a.scores?.aeo?.value ?? null,
    geo: a.scores?.geo?.value ?? null,
    overall: a.overall?.value ?? null,
  }));

  // Unique deploy dates for reference lines
  const deployDates = Array.from(
    new Set(attributions.map((attr) => fmtDate(String(attr['deployedAt'] ?? '')))),
  );

  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: '#94A3B8' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11, fill: '#94A3B8' }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          contentStyle={{
            fontSize: 12,
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            background: '#fff',
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
          iconType="line"
        />

        {/* Deployment reference lines */}
        {deployDates.map((date) => (
          <ReferenceLine
            key={date}
            x={date}
            stroke="#94A3B8"
            strokeDasharray="4 3"
            strokeWidth={1.5}
          />
        ))}

        <Line type="monotone" dataKey="overall" stroke="#334155" strokeWidth={2} dot={false} name="AI Visibility Score" />
        <Line type="monotone" dataKey="seo" stroke="#3B82F6" strokeWidth={1.5} dot={false} name="SEO" />
        <Line type="monotone" dataKey="aeo" stroke="#D97706" strokeWidth={1.5} dot={false} name="AEO" />
        <Line type="monotone" dataKey="geo" stroke="#10B981" strokeWidth={1.5} dot={false} name="GEO" />
      </LineChart>
    </ResponsiveContainer>
  );
}
